import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { GenericValidationsService } from 'src/app/Service/generic-validations.service';
import { SevaProfileService } from 'src/app/Service/seva-profile.service';
import { TranslationService } from 'src/app/Service/translation.service';
import { CONSTANTS } from '../../../../Core/Constant';
import { environment } from 'src/environments/environment';

interface LanguageItem {
  CompletionPercentage: number;
  IsActive: boolean;
  Id: number;
  SevaProfileDisplayUID: string;
  LanguageName: string;
  SpeakingProficiency: string;
  ReadingProficiency: string;
  WritingProficiency: string;
  SaveMode: number;
}

@Component({
  selector: 'app-langauge',
  templateUrl: './langauge.component.html',
  styleUrls: ['./langauge.component.scss']
})
export class LangaugeComponent {

  @Input() applicantData: any;
  @Input() isPreviewMode: boolean = false;
  @Output() formGroupChange = new EventEmitter<FormGroup>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();

  languageProficiencyForm!: FormGroup;
  submitted = false;
  lastSubmitMode: number | null = null;
  SaveMode = CONSTANTS.SAVE_MODE;
  isJsonShow = environment.isJsonShow;
  isEditable: boolean = true; // Flag to track if the form is editable

  languageOptions: any[] = [];


  proficiencyOptions = [
    { value: 'A', label: 'A: 75 – 100%' },
    { value: 'B', label: 'B: 50 – 75%' },
    { value: 'C', label: 'C: 0 - 50%' },
    { value: 'D', label: 'D: None' }
  ];

  constructor(
    private toastr: ToastrService,
    private api: SevaProfileService,
    private fb: FormBuilder,
    public genericValidations: GenericValidationsService,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    // Load saved language preference
 
    
    // Initialize proficiency options with translations
    this.updateProficiencyOptions();
    
    if (!this.isPreviewMode) {
      this.initializeForm();
      this.getLanguageOptions();
    }
  }

  // Get translation for current language
  getTranslation(key: string): string {
    return this.translationService.getTranslation(key);
  }



  // Update proficiency options with translated labels
  private updateProficiencyOptions(): void {
    this.proficiencyOptions = [
      { value: 'A', label: this.getTranslation('language.proficiencyA') },
      { value: 'B', label: this.getTranslation('language.proficiencyB') },
      { value: 'C', label: this.getTranslation('language.proficiencyC') },
      { value: 'D', label: this.getTranslation('language.proficiencyD') }
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes['applicantData']?.currentValue) {
      this.initializeForm();
      let  languages:any = { "languages": this.applicantData}  // 👈 your form expects "languages"
      this.languageProficiencyForm.patchValue(languages);
      this.languageProficiencyForm.disable();
    }
  }

  /** ---------- API Calls ---------- */
  getLanguageOptions() {
    this.api.apiCall('AppData/GetOptionSet?OptionSetName=IndianLanguageOptions', 'GET')
      .subscribe({
        next: (response: any) => {
          this.languageOptions = Array.isArray(response.Data) ? response.Data : [];
          this.getApplicantData();
        },
        error: () => {
          this.languageOptions = [];
        }
      });
  }

  getApplicantData() {
    this.api.apiCall('UserLanguages/GetUserLangauges', 'GET').subscribe((response: any) => {
      if (response?.Data) {
        this.genericValidations.setProgressPercentage(response.Data.CompletionPercentage);
        
        // Store IsEditable flag from response
        this.isEditable = response.Data.IsEditable !== false; // Default to true if not provided
  
        this.languageProficiencyForm.patchValue({SaveMode:response.Data.SaveMode});
        // Disable form if not editable
        if (!this.isEditable) {
          this.languageProficiencyForm.disable();
        } else {
          this.languageProficiencyForm.enable();
        }
        
        if (Array.isArray(response.Data.UserLanguages)) {
          this.populateFormArray(response.Data.UserLanguages);
        } else {
          this.languageProficiencyForm.patchValue({languages:response.Data.UserLanguages,SaveMode:this.SaveMode.SAVE});
        }
      } else {
        // If no data or empty data, populate with default Hindi and English rows
        this.populateFormArray([]);
      }
    });
  }

  /** ---------- Form Setup ---------- */
  initializeForm() {
    this.languageProficiencyForm = this.fb.group({
      SaveMode:[],
      languages: this.fb.array([])
    });

    // Ensure Hindi & English rows
    this.addLanguageRow({ LanguageName: 'Hindi' }, true);
    this.addLanguageRow({ LanguageName: 'English' }, true);

    this.formGroupChange.emit(this.languageProficiencyForm);
  }

  get languagesArray(): FormArray {
    return this.languageProficiencyForm.get('languages') as FormArray;
  }

  private buildLanguageForm(data?: Partial<LanguageItem>): FormGroup {
    return this.fb.group({
      CompletionPercentage: [data?.CompletionPercentage || 0],
      IsActive: [data?.IsActive ?? true],
      Id: [data?.Id || 0],
      SevaProfileDisplayUID: [data?.SevaProfileDisplayUID || ''],
      LanguageName: [data?.LanguageName || '', Validators.required],
      SpeakingProficiency: [data?.SpeakingProficiency || '', Validators.required],
      ReadingProficiency: [data?.ReadingProficiency || '', Validators.required],
      WritingProficiency: [data?.WritingProficiency || '', Validators.required],
      SaveMode: [data?.SaveMode || 0]
    });
  }

  addLanguageRow(languageData?: Partial<LanguageItem>, isInitialLoad: boolean = false) {
    // Prevent adding empty rows (only for user click, not initial load)
    if (!isInitialLoad && this.languagesArray.length > 0) {
      const lastRow = this.languagesArray.at(this.languagesArray.length - 1) as FormGroup;
      if (lastRow.invalid) {
        this.markAllAsTouched();
        this.toastr.warning(this.getTranslation('validation.completeLastRow'));
        return;
      }
    }
    this.languagesArray.push(this.buildLanguageForm(languageData));
  }

  populateFormArray(languages: LanguageItem[]) {
    this.languagesArray.clear();
    
    // If languages array is empty or has less than 2 items, add default Hindi and English
    if (languages.length === 0) {
      // Add Hindi as first row
      this.addLanguageRow({ LanguageName: 'Hindi' }, true);
      // Add English as second row
      this.addLanguageRow({ LanguageName: 'English' }, true);
    } else {
      // If there are existing languages, add them first
      languages.forEach((lang, idx) => this.addLanguageRow(lang, true));
      
      // Ensure we have at least Hindi and English
      const hasHindi = languages.some(lang => lang.LanguageName?.toLowerCase() === 'hindi');
      const hasEnglish = languages.some(lang => lang.LanguageName?.toLowerCase() === 'english');
      
      // Add Hindi if not present
      if (!hasHindi) {
        this.languagesArray.insert(0, this.buildLanguageForm({ LanguageName: 'Hindi' }));
      }
      
      // Add English if not present (after Hindi)
      if (!hasEnglish) {
        const insertIndex = hasHindi ? 1 : 0;
        this.languagesArray.insert(insertIndex, this.buildLanguageForm({ LanguageName: 'English' }));
      }
    }
  }

  removeLanguageRow(index: number) {
    // Prevent removing Hindi and English rows (first two rows)
    if (index < 2) {
      this.toastr.warning(this.getTranslation('validation.hindiEnglishRequired'));
      return;
    }
    
    if (this.languagesArray.length <= 2) {
      this.toastr.warning(this.getTranslation('validation.minimumLanguagesRequired'));
      return;
    }
    
    this.languagesArray.removeAt(index);
  }

  isRowRequired(index: number): boolean {
    // First two rows (Hindi and English) are always required
    return index < 2;
  }

  /** ---------- Submit ---------- */
  private markAllAsTouched() {
    this.languagesArray.controls.forEach(control => control.markAllAsTouched());
  }

  onSubmit(mode: number): void {
    this.lastSubmitMode = mode;
    if (mode === this.SaveMode.SAVE) {
      this.submitted = true;
    }

    if (this.languageProficiencyForm.invalid && mode === this.SaveMode.SAVE) {
      this.markAllAsTouched();
      this.toastr.warning(this.getTranslation('validation.completeAllFields'));

      return;
    }

    const languagesData = this.languagesArray.value.map((lang: LanguageItem) => ({
      ...lang,
      SaveMode: mode
    }));

    this.addUpdate(languagesData, mode);
  }

  addUpdate(reqBody: any, mode: number) {
    reqBody.SaveMode = mode;
    this.api.apiCall('UserLanguages/SaveUserLangauges', 'POST', reqBody).subscribe((response: any) => {
      this.toastr.success(response?.Message || 'Saved successfully');
      if (mode === this.SaveMode.SAVE) {
        this.nextStep.emit();
      } else {
        this.getApplicantData();
      }
    });
  }

  goToBack() {
    this.previous.emit();
  }


  getAvailableLanguages(currentIndex: number) {
    // All selected languages in form
    const selected = this.languagesArray.controls
      .map((ctrl, idx) => idx !== currentIndex ? ctrl.get('LanguageName')?.value : null)
      .filter(v => !!v);
  
    return this.languageOptions.filter(lang => 
      !['Hindi', 'English'].includes(lang.OptionText) &&  // exclude Hindi & English
      !selected.includes(lang.OptionValue)                // exclude already chosen
    );
  }
}
